import { useState } from 'react';
import { Settings, User, Lock, Bell, Palette, Save } from 'lucide-react';

interface AdminSettings {
  notifications: { email: boolean; push: boolean; digest: boolean };
  preferences: { theme: 'light' | 'dark' | 'auto'; language: string; timezone: string };
}

const CARD = "bg-white/[0.04] border border-white/[0.07] rounded-2xl p-5 backdrop-blur-sm";
const INPUT = "w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-primary/50 focus:bg-white/[0.07] transition-all disabled:opacity-40 disabled:cursor-not-allowed";

function SettingsPage() {
  const [activeTab, setActiveTab] = useState<'profile' | 'security' | 'notifications' | 'preferences'>('profile');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [settings, setSettings] = useState<AdminSettings>({
    notifications: { email: true, push: true, digest: false },
    preferences: { theme: 'dark', language: 'en', timezone: 'America/New_York' }
  });

  const handleSave = async () => {
    setSaving(true); setSuccess(false);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setSaving(false); setSuccess(true);
    setTimeout(() => setSuccess(false), 3000);
  };

  const tabs = [
    { id: 'profile' as const, label: 'Profile', icon: <User size={15} /> },
    { id: 'security' as const, label: 'Security', icon: <Lock size={15} /> },
    { id: 'notifications' as const, label: 'Notifications', icon: <Bell size={15} /> },
    { id: 'preferences' as const, label: 'Preferences', icon: <Palette size={15} /> },
  ];

  const ToggleSwitch = ({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) => (
    <button
      role="switch" aria-checked={checked} onClick={() => onChange(!checked)}
      className={`relative inline-flex w-10 h-5.5 rounded-full transition-colors flex-shrink-0 ${checked ? 'bg-primary' : 'bg-white/20'}`}
      style={{ height: '22px', width: '40px' }}
    >
      <span className={`absolute top-0.5 left-0.5 w-[18px] h-[18px] rounded-full bg-white shadow-sm transition-transform ${checked ? 'translate-x-[18px]' : 'translate-x-0'}`} />
    </button>
  );

  const OptionCard = ({ title, desc, checked, onChange }: { title: string; desc: string; checked: boolean; onChange: (v: boolean) => void }) => (
    <div className="flex items-center justify-between bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
      <div>
        <p className="text-sm font-medium text-white/80">{title}</p>
        <p className="text-xs text-white/40 mt-0.5">{desc}</p>
      </div>
      <ToggleSwitch checked={checked} onChange={onChange} />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/20 border border-primary/30 flex items-center justify-center">
          <Settings size={18} className="text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">Admin Settings</h1>
          <p className="text-white/40 text-sm mt-0.5">Manage your admin account preferences</p>
        </div>
      </div>

      {success && (
        <div className="bg-green/10 border border-green/20 text-green rounded-lg px-4 py-3 text-sm">
          ✅ Settings saved successfully!
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-5">
        {/* Tab nav */}
        <div className="flex md:flex-col gap-1">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-primary/15 text-primary border border-primary/20'
                  : 'text-white/50 hover:text-white/80 hover:bg-white/[0.04]'
              }`}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className={CARD}>
          {activeTab === 'profile' && (
            <div className="space-y-4">
              <div><h2 className="text-base font-semibold text-white">Profile Information</h2><p className="text-white/40 text-xs mt-0.5">Update your personal information</p></div>
              <div><label className="block text-xs font-medium text-white/50 mb-1.5">Full Name</label><input type="text" className={INPUT} defaultValue="Super Admin" placeholder="Enter your full name" /></div>
              <div><label className="block text-xs font-medium text-white/50 mb-1.5">Email Address</label><input type="email" className={INPUT} defaultValue="admin@wavebudget.com" placeholder="Enter your email" /></div>
              <div>
                <label className="block text-xs font-medium text-white/50 mb-1.5">Role</label>
                <input type="text" className={INPUT} value="Super Admin" disabled />
                <p className="text-white/30 text-xs mt-1">Your role cannot be changed</p>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="space-y-4">
              <div><h2 className="text-base font-semibold text-white">Security Settings</h2><p className="text-white/40 text-xs mt-0.5">Manage your password and security options</p></div>
              <div><label className="block text-xs font-medium text-white/50 mb-1.5">Current Password</label><input type="password" className={INPUT} placeholder="Enter current password" /></div>
              <div><label className="block text-xs font-medium text-white/50 mb-1.5">New Password</label><input type="password" className={INPUT} placeholder="Enter new password" /></div>
              <div><label className="block text-xs font-medium text-white/50 mb-1.5">Confirm New Password</label><input type="password" className={INPUT} placeholder="Confirm new password" /></div>
              <div className="pt-2">
                <h3 className="text-sm font-semibold text-white mb-3">Two-Factor Authentication</h3>
                <OptionCard title="Enable 2FA" desc="Add an extra layer of security to your account" checked={false} onChange={() => {}} />
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="space-y-4">
              <div><h2 className="text-base font-semibold text-white">Notification Preferences</h2><p className="text-white/40 text-xs mt-0.5">Choose what notifications you want to receive</p></div>
              <OptionCard title="Email Notifications" desc="Receive important updates via email"
                checked={settings.notifications.email}
                onChange={(v) => setSettings({ ...settings, notifications: { ...settings.notifications, email: v } })} />
              <OptionCard title="Push Notifications" desc="Get instant alerts in your browser"
                checked={settings.notifications.push}
                onChange={(v) => setSettings({ ...settings, notifications: { ...settings.notifications, push: v } })} />
              <OptionCard title="Daily Digest" desc="Receive a summary of daily activities"
                checked={settings.notifications.digest}
                onChange={(v) => setSettings({ ...settings, notifications: { ...settings.notifications, digest: v } })} />
            </div>
          )}

          {activeTab === 'preferences' && (
            <div className="space-y-4">
              <div><h2 className="text-base font-semibold text-white">Display Preferences</h2><p className="text-white/40 text-xs mt-0.5">Customize your admin panel experience</p></div>
              <div>
                <label className="block text-xs font-medium text-white/50 mb-1.5">Theme</label>
                <select className={INPUT} value={settings.preferences.theme} onChange={(e) => setSettings({ ...settings, preferences: { ...settings.preferences, theme: e.target.value as any } })}>
                  <option value="light">Light</option><option value="dark">Dark</option><option value="auto">Auto (System)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-white/50 mb-1.5">Language</label>
                <select className={INPUT} value={settings.preferences.language} onChange={(e) => setSettings({ ...settings, preferences: { ...settings.preferences, language: e.target.value } })}>
                  <option value="en">English</option><option value="es">Spanish</option><option value="fr">French</option><option value="de">German</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-white/50 mb-1.5">Timezone</label>
                <select className={INPUT} value={settings.preferences.timezone} onChange={(e) => setSettings({ ...settings, preferences: { ...settings.preferences, timezone: e.target.value } })}>
                  <option value="America/New_York">Eastern Time (ET)</option>
                  <option value="America/Chicago">Central Time (CT)</option>
                  <option value="America/Denver">Mountain Time (MT)</option>
                  <option value="America/Los_Angeles">Pacific Time (PT)</option>
                  <option value="UTC">UTC</option>
                </select>
              </div>
            </div>
          )}

          <div className="mt-6 pt-4 border-t border-white/[0.06]">
            <button onClick={handleSave} disabled={saving}
              className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white rounded-lg px-5 py-2.5 text-sm font-medium transition-colors disabled:opacity-50">
              {saving ? (
                <><div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" /> Saving...</>
              ) : (
                <><Save size={15} /> Save Changes</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;
